import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Callout, buttonClass, inputClass, textareaClass } from '@/components/ui';
import type { ProductListing } from '@/lib/stock-types';

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
}

/**
 * What the shop says about a style: its description, its tags and its photos.
 *
 * Drawn on the style page (spec 010). It was the panel on Products, and moved
 * rather than copied, so a style has one editor wherever it is opened.
 */
export function ProductEditor(
  { product, onSaved }:
  { product: Pick<ProductListing, 'id' | 'name' | 'description' | 'tags' | 'product_images'>; onSaved?: () => void },
) {
  const qc = useQueryClient();
  const [description, setDescription] = useState(product.description ?? '');
  const [tags, setTags] = useState((product.tags ?? []).join(', '));
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['admin', 'product-listing'] });
    onSaved?.();
  };

  const save = useMutation({
    mutationFn: () => call(`/api/products/${product.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        description,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      }),
    }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  const addImage = useMutation({
    mutationFn: () => call(`/api/products/${product.id}/images`, {
      method: 'POST',
      body: JSON.stringify({ url: imageUrl, alt: imageAlt, sort: product.product_images.length }),
    }),
    onSuccess: () => { setImageUrl(''); setImageAlt(''); setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  const removeImage = useMutation({
    mutationFn: (id: string) => call(`/api/product-images/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="px-4 py-4">
          {error && <Callout tone="danger" title={error} className="mb-3" />}

          <label className="block text-[11px] font-medium text-muted" htmlFor={`d-${product.id}`}>
            What the shop says about it
          </label>
          <textarea
            id={`d-${product.id}`}
            rows={4}
            className={`${textareaClass} mt-1`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Heavy 240gsm cotton, boxy fit, pre-shrunk."
          />

          <label className="mt-3 block text-[11px] font-medium text-muted" htmlFor={`t-${product.id}`}>
            Tags, separated by commas. They become slugs: &ldquo;New In&rdquo; is saved as new-in
          </label>
          <input
            id={`t-${product.id}`}
            className={`${inputClass} mt-1`}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="new-in, best-seller"
          />

          <div className="mt-3">
            <button
              type="button"
              className={buttonClass('primary', 'sm')}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving' : 'Save'}
            </button>
          </div>

          <h3 className="mt-5 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
            Photographs
          </h3>
          {product.product_images.length === 0 ? (
            <p className="mt-1 text-[12.5px] text-muted">
              None yet. A product with no photograph is a grey box on the shop.
            </p>
          ) : (
            <ul className="mt-2 flex flex-wrap gap-2">
              {product.product_images.map((img) => (
                <li key={img.id} className="w-[104px]">
                  <img
                    src={img.url}
                    alt={img.alt}
                    className="h-[104px] w-[104px] rounded-[4px] border border-line object-cover"
                  />
                  <p className="mt-1 truncate text-[10.5px] text-muted" title={img.alt}>{img.alt}</p>
                  <button
                    type="button"
                    className="mt-0.5 text-[10.5px] text-[color:var(--color-danger-ink)] underline"
                    onClick={() => removeImage.mutate(img.id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              className={inputClass}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="/designs/template1.png"
              aria-label={`Image address for ${product.name}`}
            />
            <input
              className={inputClass}
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              placeholder="Front, flat lay"
              aria-label={`What the image shows, for ${product.name}`}
            />
            <button
              type="button"
              className={buttonClass('secondary', 'sm')}
              disabled={addImage.isPending}
              onClick={() => addImage.mutate()}
            >
              Add
            </button>
          </div>
          {/* Said where somebody is about to type, not in a help page. */}
      <p className="mt-1.5 text-[11px] text-muted">
        The second box is what the image shows. Without it the photograph is
        invisible to a screen reader and to a search engine.
      </p>
    </div>
  );
}
